import { useState, useRef, useEffect } from 'react';
import { Languages } from 'lucide-react';
import { useLanguageStore } from '@/stores/languageStore';
import { LANGUAGE_OPTIONS } from '@/lib/translations';
import { cn } from '@/lib/utils';

export function LanguageToggle() {
  const { language, setLanguage } = useLanguageStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const current = LANGUAGE_OPTIONS.find((l) => l.code === language) || LANGUAGE_OPTIONS[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-md transition-colors',
          'hover:bg-accent hover:text-accent-foreground text-muted-foreground',
        )}
        aria-label="Change language"
        title="Change language"
      >
        <Languages className="h-4 w-4" />
        <span className="text-xs">{current.label}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 rounded-md border border-border bg-[#080a18] shadow-lg z-50">
          {LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.code}
              onClick={() => {
                setLanguage(option.code);
                setOpen(false);
              }}
              className={cn(
                'flex items-center justify-between w-full px-3 py-2 text-sm transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                language === option.code && 'bg-accent/50 font-medium',
              )}
            >
              <span>{option.nativeLabel}</span>
              <span className="text-xs text-muted-foreground">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
